import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { Pagina } from '../../../../service/types/cursos.types';
import { GcsService } from '../../../../service/gcs.service';
import { getYoutubeEmbedUrl } from '../../../../service/utils/youtube';
import TestRunner from './TestRunner';

interface PaginaRendererProps {
  pagina: Pagina;
  onTestAprobado?: () => void;
}

/** Resuelve pagina.urlArchivo a una URL usable: firmada si es un path de GCS, tal cual si es una URL externa. */
function useArchivoUrl(urlArchivo?: string) {
  const [url, setUrl] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (!urlArchivo) {
      setUrl(null);
      return;
    }
    if (!GcsService.isManagedPath(urlArchivo)) {
      setUrl(urlArchivo);
      return;
    }
    let cancelado = false;
    setCargando(true);
    GcsService.getSignedUrl(urlArchivo)
      .then((signedUrl) => {
        if (!cancelado) setUrl(signedUrl);
      })
      .catch(() => {
        if (!cancelado) setUrl(null);
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [urlArchivo]);

  return { url, cargando };
}

export default function PaginaRenderer({ pagina, onTestAprobado }: PaginaRendererProps) {
  const { url, cargando } = useArchivoUrl(pagina.urlArchivo);

  switch (pagina.tipo) {
    case 'video':
      return (
        <div>
          <h5>{pagina.titulo}</h5>
          {pagina.contenido && <p style={{ whiteSpace: 'pre-wrap' }}>{pagina.contenido}</p>}
          {cargando && (
            <p className="text-muted">
              <Spinner animation="border" size="sm" /> Cargando video...
            </p>
          )}
          {!cargando && url && (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video controls src={url} style={{ width: '100%', maxHeight: '70vh' }} />
          )}
          {!cargando && !url && <p className="text-muted">Esta página no tiene un video asociado.</p>}
        </div>
      );
    case 'youtube': {
      const embedUrl = pagina.urlArchivo ? getYoutubeEmbedUrl(pagina.urlArchivo) : null;
      return (
        <div>
          <h5>{pagina.titulo}</h5>
          {pagina.contenido && <p style={{ whiteSpace: 'pre-wrap' }}>{pagina.contenido}</p>}
          {embedUrl ? (
            <>
              <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                <iframe
                  src={embedUrl}
                  title={pagina.titulo}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                />
              </div>
              <p className="text-muted small mt-2">Si el video no es visible avisar al administrador.</p>
            </>
          ) : (
            <p className="text-muted">Esta página no tiene un video de YouTube válido asociado.</p>
          )}
        </div>
      );
    }
    case 'imagen':
      return (
        <div>
          <h5>{pagina.titulo}</h5>
          {pagina.contenido && <p style={{ whiteSpace: 'pre-wrap' }}>{pagina.contenido}</p>}
          {cargando && (
            <p className="text-muted">
              <Spinner animation="border" size="sm" /> Cargando imagen...
            </p>
          )}
          {!cargando && url && (
            <img src={url} alt={pagina.titulo} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
          )}
          {!cargando && !url && <p className="text-muted">Esta página no tiene una imagen asociada.</p>}
        </div>
      );
    case 'documento':
      return (
        <div>
          <h5>{pagina.titulo}</h5>
          {pagina.contenido && <p style={{ whiteSpace: 'pre-wrap' }}>{pagina.contenido}</p>}
          {cargando && (
            <p className="text-muted">
              <Spinner animation="border" size="sm" /> Preparando descarga...
            </p>
          )}
          {!cargando && url && (
            <a href={url} target="_blank" rel="noopener noreferrer">
              Descargar documento
            </a>
          )}
          {!cargando && !url && <p className="text-muted">Esta página no tiene un documento asociado.</p>}
        </div>
      );
    case 'descripcion':
      return (
        <div>
          <h5>{pagina.titulo}</h5>
          <p style={{ whiteSpace: 'pre-wrap' }}>{pagina.contenido}</p>
        </div>
      );
    case 'test':
      return (
        <div>
          <h5>{pagina.titulo}</h5>
          {pagina.contenido && <p style={{ whiteSpace: 'pre-wrap' }}>{pagina.contenido}</p>}
          <TestRunner paginaId={pagina.id} onAprobado={onTestAprobado} />
        </div>
      );
    default:
      return null;
  }
}
